import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Alert
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  MedicalServices,
  PendingActions,
  Assessment,
  Hotel,
  TrendingUp,
  EventRepeat,
  ExitToApp,
  CalendarToday,
  People,
  LocalHospital
} from '@mui/icons-material';
import { dashboardAPI } from '../services/api';
import DoctorConsultation from './DoctorConsultation';
import IPDPatientsList from './doctor/IPDPatientsList';
import DayCarePatientsList from './doctor/DayCarePatientsList';
import FollowUpConsultations from './doctor/FollowUpConsultations';
import PatientList from './PatientList';

function DoctorDashboard() {
  const location = useLocation();
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 0);
  const [loading, setLoading] = useState(true);

  // Discharging patients state
  const [dischargingPatients, setDischargingPatients] = useState([]);
  const [dischargingDate, setDischargingDate] = useState(new Date().toISOString().split('T')[0]);
  const [dischargingLoading, setDischargingLoading] = useState(false);
  const [dischargingError, setDischargingError] = useState('');

  useEffect(() => {
    loadDashboardData();
    loadDischargingPatients(dischargingDate);
  }, []);

  const loadDashboardData = async () => {
    try {
      const response = await dashboardAPI.getDoctorDashboard();
      setDashboardData(response.data.data || response.data);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDischargingPatients = async (date) => {
    setDischargingLoading(true);
    setDischargingError('');
    try {
      const response = await dashboardAPI.getDischargingPatients(date);
      const data = response.data.data || response.data;
      setDischargingPatients(data.patients || []);
    } catch (err) {
      console.error('Failed to load discharging patients:', err);
      setDischargingError('Failed to load discharging patients');
    } finally {
      setDischargingLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDischargingDate(newDate);
    loadDischargingPatients(newDate);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const StatCard = ({ title, value, icon: Icon, gradient }) => (
    <Card sx={{
      background: gradient,
      color: 'white',
      height: '100%'
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
              {title}
            </Typography>
            <Typography variant="h3" fontWeight="bold">
              {value}
            </Typography>
          </Box>
          <Icon sx={{ fontSize: 48, opacity: 0.8 }} />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab
            icon={<DashboardIcon />}
            iconPosition="start"
            label="Dashboard"
          />
          <Tab
            icon={<MedicalServices />}
            iconPosition="start"
            label="Consultations"
          />
          <Tab
            icon={<EventRepeat />}
            iconPosition="start"
            label="Follow-ups"
          />
          <Tab
            icon={<Hotel />}
            iconPosition="start"
            label="IPD Patients"
          />
          <Tab
            icon={<LocalHospital />}
            iconPosition="start"
            label="Day Care"
          />
          <Tab
            icon={<People />}
            iconPosition="start"
            label="Patient Records"
          />
        </Tabs>
      </Box>

      {activeTab === 0 ? (
        <Box>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Today's Consultations"
                value={dashboardData?.summary?.todayConsultations || 0}
                icon={MedicalServices}
                gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Pending Consultations"
                value={dashboardData?.summary?.pendingConsultations || 0}
                icon={PendingActions}
                gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Consultations"
                value={dashboardData?.summary?.totalConsultations || 0}
                icon={Assessment}
                gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="IPD Suggestions"
                value={dashboardData?.summary?.ipdSuggestions || 0}
                icon={Hotel}
                gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              />
            </Grid>
          </Grid>

           {/* Discharging Patients Section */}
          <Card elevation={2} sx={{ mt: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ExitToApp sx={{ color: 'error.main', mr: 1 }} />
                  <Typography variant="h6" fontWeight="bold">
                    Patients Discharging
                  </Typography>
                </Box>
                <TextField
                  type="date"
                  label="Discharge Date"
                  value={dischargingDate}
                  onChange={handleDateChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarToday fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 200 }}
                />
              </Box>

              {dischargingError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDischargingError('')}>
                  {dischargingError}
                </Alert>
              )}

              {dischargingLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : dischargingPatients.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography color="text.secondary">
                    No patients scheduled for discharge on {new Date(dischargingDate).toLocaleDateString('en-GB')}
                  </Typography>
                </Paper>
              ) : (
                <>
                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={`${dischargingPatients.length} Patient${dischargingPatients.length !== 1 ? 's' : ''}`}
                      color="error"
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      scheduled for discharge on {new Date(dischargingDate).toLocaleDateString('en-GB')}
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell><strong>Patient Code</strong></TableCell>
                          <TableCell><strong>Patient Name</strong></TableCell>
                          <TableCell><strong>Age/Gender</strong></TableCell>
                          <TableCell><strong>Room</strong></TableCell>
                          <TableCell><strong>Category</strong></TableCell>
                          <TableCell><strong>Admission Date</strong></TableCell>
                          <TableCell align="right"><strong>Days Admitted</strong></TableCell>
                          <TableCell align="right"><strong>Planned Days</strong></TableCell>
                          <TableCell align="right"><strong>Total Charges</strong></TableCell>
                          <TableCell><strong>Contact</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dischargingPatients.map((patient) => (
                          <TableRow key={patient.admission_id} hover>
                            <TableCell>{patient.patient_code}</TableCell>
                            <TableCell>
                              {patient.first_name} {patient.last_name}
                            </TableCell>
                            <TableCell>
                              {patient.age}Y / {patient.gender}
                            </TableCell>
                            <TableCell>{patient.room_number}</TableCell>
                            <TableCell>
                              <Chip
                                label={patient.category_name}
                                color={
                                  patient.category_name?.toLowerCase() === 'hotel' ? 'success' :
                                  patient.category_name?.toLowerCase() === 'cottage' ? 'primary' :
                                  'info'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(patient.admission_date).toLocaleDateString('en-GB')}
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={patient.days_admitted} size="small" color="default" />
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={patient.planned_days} size="small" color="primary" />
                            </TableCell>
                            <TableCell align="right">
                              ₹{parseFloat(patient.total_charges || 0).toFixed(2)}
                            </TableCell>
                            <TableCell>{patient.phone}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>

          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Recent Consultations
              </Typography>
              {dashboardData?.recentConsultations?.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography color="text.secondary">
                    No consultations yet
                  </Typography>
                </Paper>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Patient Code</strong></TableCell>
                        <TableCell><strong>Patient Name</strong></TableCell>
                        <TableCell><strong>Diagnosis</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardData?.recentConsultations?.map((consultation) => (
                        <TableRow key={consultation.id} hover>
                          <TableCell>
                            {new Date(consultation.consultation_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{consultation.patient_code}</TableCell>
                          <TableCell>
                            {consultation.first_name} {consultation.last_name}
                          </TableCell>
                          <TableCell>
                            {consultation.diagnosis?.substring(0, 50)}
                            {consultation.diagnosis?.length > 50 ? '...' : ''}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={consultation.status}
                              color={consultation.status === 'completed' ? 'success' : 'warning'}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUp sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Top Prescribed Treatments
                    </Typography>
                  </Box>
                  {dashboardData?.topTreatments?.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography color="text.secondary">
                        No data available
                      </Typography>
                    </Paper>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell><strong>Treatment Name</strong></TableCell>
                            <TableCell align="right"><strong>Count</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dashboardData?.topTreatments?.map((treatment, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{treatment.name}</TableCell>
                              <TableCell align="right">
                                <Chip label={treatment.prescribed_count} size="small" color="primary" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Assessment sx={{ color: 'primary.main', mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                      Monthly Consultation Stats
                    </Typography>
                  </Box>
                  {dashboardData?.monthlyStats?.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography color="text.secondary">
                        No data available
                      </Typography>
                    </Paper>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell><strong>Month</strong></TableCell>
                            <TableCell align="right"><strong>Consultations</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dashboardData?.monthlyStats?.map((stat, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{stat.month}</TableCell>
                              <TableCell align="right">
                                <Chip label={stat.consultation_count} size="small" color="secondary" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
         
        </Box>
      ) : activeTab === 1 ? (
        <DoctorConsultation />
      ) : activeTab === 2 ? (
        <FollowUpConsultations />
      ) : activeTab === 3 ? (
        <IPDPatientsList />
      ) : activeTab === 4 ? (
        <DayCarePatientsList />
      ) : (
        <PatientList />
      )}
    </Box>
  );
}

export default DoctorDashboard;
